/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 메뉴 클래스를 정의한다.
 *
 * @file /src/components/menu/Menu.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 8. 30.
 */
import * as parent from '@base/Component';
import * as title from '@elements/title/Bar';
import { Item } from './elements/Item';
import * as items from './elements/Items';
import * as button from './elements/items/Button';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Renderer } from '@utils/Renderer';
import './Menu.css';

export { items };

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 메뉴아이템이 보이기 시작할 때
     */
    beforeShow?: (me: T) => void;

    /**
     * 메뉴아이템이 보일 때
     */
    show?: (me: T) => void;

    /**
     * 메뉴아이템이 숨겨질 때
     */
    hide?: (me: T) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 메뉴를 보일 대상 DOM
     */
    $target?: Dom;

    /**
     * 메뉴를 출력할 축
     */
    direction?: 'x' | 'y';

    /**
     * 메뉴 제목 (텍스트 또는 Title 엘리먼트)
     */
    title?: title.Properties<title.Bar> | title.Properties<title.Bar>['text'];

    /**
     * 메뉴아이템
     */
    menus?: items.Properties[];

    /**
     * 숨겨질 때 메뉴를 제거할 지 여부 (메뉴가 재사용되는 경우에는 사용하지 마세요.)
     */
    removeOnHide?: boolean;

    /**
     * @todo
     * 메뉴가 표시될 동안 배경 UI 를 잠글지 여부 (서브메뉴에서는 적용되지 않는다.)
     */
    backdrop?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Menu extends parent.Component {
    /**
     * 현재 보이고 있는 최상위 메뉴
     */
    static currentMenu: Menu | null = null;

    /**
     * 컴포넌트 타입
     */
    type = 'menu';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'menu';

    /**
     * 메뉴 제목
     */
    title: title.Bar | null;

    /**
     * 메뉴를 출력할 기준이 되는 DOM
     */
    $target: Dom = null;

    /**
     * 메뉴를 출력할 축
     */
    direction: 'x' | 'y' = 'y';

    /**
     * 숨겨질 때 메뉴를 제거할 지 여부
     */
    removeOnHide: boolean;

    /**
     * 메뉴가 표시될 동안 배경 UI 를 잠글지 여부 (서브메뉴에서는 적용되지 않는다.)
     */
    backdrop: boolean;

    /**
     * 메뉴아이템
     */
    menus: Item[] = [];

    /**
     * 헤더 DOM
     */
    $header: Dom = null;

    /**
     * showAt() 호출 시 생성되는 임시 anchor DOM
     */
    $showAtAnchor: Dom = null;

    /**
     * 메뉴 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(
        public properties: Pick<
            Properties<Menu>,
            'id' | '$target' | 'direction' | 'title' | 'menus' | 'backdrop' | 'removeOnHide' | 'maxWidth' | 'listeners'
        > = null
    ) {
        super(properties);

        this.keyboard = true;

        this.$target = this.properties?.$target ?? null;
        this.direction = this.properties?.direction === 'x' ? 'x' : 'y';
        this.backdrop = this.properties?.backdrop === true;
        this.removeOnHide = this.properties?.removeOnHide === true;
        this.title = this.createTitle(this.properties?.title);
        this.maxWidth = this.properties?.maxWidth ?? 300;

        this.layout = 'auto';
        this.border = false;

        this.initMenus();

        if (this.$target !== null) {
            this.setTarget(this.$target, this.direction);
        }
    }

    /**
     * 메뉴아이템을 초기화한다.
     */
    initMenus(): void {
        this.menus ??= [];
        for (const item of this.properties?.menus ?? []) {
            const menu = items.Items.get(item);
            menu.setComponent(this);
            this.menus.push(menu);
        }
    }

    /**
     * 메뉴가 보일 대상 DOM 을 지정한다.
     *
     * @param $target - 대상 DOM
     * @param direction - 메뉴방향
     */
    setTarget($target: Dom, direction: 'x' | 'y' | null = null): void {
        this.$target = $target;
        if (direction !== null) {
            this.direction = direction === 'y' ? 'y' : 'x';
        }

        const anchorName = `--anchor-menu-${this.id}-${Date.now()}`;
        this.$getComponent().setStyle('position-anchor', anchorName).setAttr('data-popover', anchorName);
        this.$target.setStyle('anchor-name', anchorName);
        this.$target.setAttr('data-popover', anchorName);
    }

    /**
     * 컴포넌트 최상위 DOM 을 가져온다.
     *
     * @returns 최상위 DOM
     */
    $getComponent(): Dom {
        if (this.$component === null) {
            this.$component = super.$getComponent();

            this.$component
                .setAttr('popover', 'manual')
                .setAttr('data-direction', this.direction)
                .on('toggle', async (_, e: ToggleEvent) => {
                    if (e.newState === 'closed') {
                        await this.hide();
                    }
                });

            if (this.backdrop == true) {
                this.$component.addClass('backdrop');
            }
        }

        return this.$component;
    }

    /**
     * 헤더 DOM 을 가져온다.
     *
     * @returns 헤더 DOM
     */
    $getHeader(): Dom {
        if (this.$header === null) {
            this.$header = Html.create('div', { 'data-role': 'header' });
        }

        return this.$header;
    }

    /**
     * 제목 엘리먼트를 생성한다.
     *
     * @param properties - 제목설정
     * @returns 제목 엘리먼트
     */
    protected createTitle(
        properties: title.Properties<title.Bar> | title.Properties<title.Bar>['text'] | undefined,
        icon: string | null = null
    ): title.Bar | null {
        if (properties === undefined) {
            return null;
        }

        let element: title.Bar | null = null;
        if (typeof properties === 'object' && 'text' in properties) {
            element = new title.Bar(properties);
        } else {
            element = new title.Bar({ text: properties });
        }
        if (icon !== null) {
            element.setIcon(icon);
        }

        element?.setComponent(this);

        return element;
    }

    /**
     * 타이틀을 설정한다.
     *
     * @param title - 타이틀
     * @param icon - 아이콘
     */
    setTitle(
        properties: title.Properties<title.Bar> | title.Properties<title.Bar>['text'] | undefined,
        icon: string | null = null
    ): title.Bar {
        this.title?.remove();

        this.title = this.createTitle(properties, icon);
        if (this.isRendered() == true) {
            this.$getHeader().append(this.title.$getElement(), 0);
            this.title.render();
        }

        return this.title;
    }

    /**
     * 타이틀을 가져온다.
     *
     * @returns 타이틀
     */
    getTitle(): title.Bar | null {
        return this.title;
    }

    /**
     * 메뉴 아이템 초기설정으로 메뉴 아이템을 추가한다.
     *
     * @param item - 메뉴 아이템 객체
     */
    addMenu(item: Omit<button.Properties<button.Button>, 'type'> & { type?: 'button' }): void;
    addMenu(item: items.Properties): void {
        const menu = items.Items.get(item);
        menu.setComponent(this);
        this.menus.push(menu);
    }

    /**
     * 메뉴 아이템을 추가한다.
     *
     * @param item - 메뉴 아이템
     */
    add(item: Item): void {
        item.setComponent(this);
        this.menus.push(item);
    }

    /**
     * 특정 메뉴명을 가진 메뉴가 존재하는지 확인한다.
     *
     * @param name - 존재여부를 확인할 메뉴명
     * @param type - 존재여부를 확인할 메뉴타입 (정의되지 않은 경우 button)
     * @returns 존재여부
     */
    hasMenuByName(name: string, type?: string): boolean {
        const variant = type ?? 'button';
        for (const menu of this.menus) {
            if (menu.variant == variant && menu.name === name) {
                return true;
            }
        }

        return false;
    }

    /**
     * 특정 메뉴명을 가진 메뉴를 가져온다.
     *
     * @param name - 가져올 메뉴명
     * @param type - 가져올 메뉴타입 (정의되지 않은 경우 button)
     * @returns 메뉴
     */
    getMenuByName<T extends items.Types = 'button'>(name: string, type?: T): items.MenuTypes<T> | null {
        const variant = type ?? 'button';
        for (const menu of this.menus) {
            if (menu.variant == variant && menu.name === name) {
                return menu as items.MenuTypes<T>;
            }
        }

        return null;
    }

    /**
     * 서브메뉴 객체를 생성한다.
     *
     * @param  properties - 서브메뉴 초기설정
     * @returns 서브메뉴
     */
    createSubMenu(properties: Menu | items.Properties[]): Menu {
        let menu: Menu;
        if (properties instanceof Menu) {
            menu = properties;
        } else {
            menu = new Menu({
                menus: properties,
            });
        }
        menu.setParent(this);
        menu.addEvent('hide', (menu) => {
            menu.getParent().focus();
        });

        return menu;
    }

    /**
     * 메뉴가 보일 수 있는 상태인지 확인한다.
     *
     * @returns 메뉴가 보일 수 있는지 여부
     */
    isDisplayable(): boolean {
        return this.title !== null || this.menus.length > 0;
    }

    /**
     * 특정 DOM 위치에 메뉴를 출력한다.
     *
     * @param is_focus - 메뉴에 포커스를 지정할지 여부
     */
    async show(is_focus: boolean = true): Promise<void> {
        if (this.isDisplayable() == false) {
            await this.hide();
            return;
        }

        if (this.getParent()?.type !== 'menu') {
            if (Menu.currentMenu !== null && Menu.currentMenu !== this) {
                await Menu.currentMenu.hide();
            }
            Menu.currentMenu = this;
        }

        await this.connect();
        await this.fireEvent('beforeShow', [this]);

        for (const item of this.menus) {
            item.fireEvent('show', [item]);
        }

        this.$getComponent().getEl().showPopover();

        await this.fireEvent('show', [this]);

        if (is_focus == true) {
            this.focus();
        }
    }

    /**
     * 마우스 클릭 위치에 메뉴를 출력한다.
     *
     * @param positionX - x 좌표
     * @param positionY - y 좌표
     * @param is_focus - 메뉴에 포커스를 지정할지 여부
     */
    async showAt(positionX: number, positionY: number, is_focus: boolean = true): Promise<void> {
        this.$showAtAnchor?.remove();
        this.$showAtAnchor = Html.create('div');
        this.$showAtAnchor
            .setStyle('position', 'fixed')
            .setStyle('left', `${positionX}px`)
            .setStyle('top', `${positionY}px`)
            .setStyle('width', '1px')
            .setStyle('height', '1px')
            .setStyle('pointer-events', 'none');
        Html.get('*[data-role=aui]').append(this.$showAtAnchor);

        this.setTarget(this.$showAtAnchor);
        await this.show(is_focus);
    }

    /**
     * 메뉴를 숨긴다.
     *
     * @parma hideAll - 상위 메뉴 전부를 숨길지 여부
     */
    async hide(hideAll: boolean = false): Promise<void> {
        if (this.isShow() == true) {
            this.$getComponent().getEl().hidePopover();
        }

        this.$showAtAnchor?.remove();
        this.$showAtAnchor = null;

        if (hideAll == true && this.getParent()?.type === 'menu') {
            await (this.getParent() as Menu).hide();
        }

        if (Menu.currentMenu === this) {
            Menu.currentMenu = null;
            await this.fireEvent('hide', [this]);
        }

        if (this.removeOnHide === true) {
            await this.remove();
        }
    }

    /**
     * 메뉴가 보이는 상태인지 확인한다.
     *
     * @returns 메뉴가 보이는지 여부
     */
    isShow(): boolean {
        if (this.isRendered() == false) {
            return false;
        }
        return this.$getComponent().getEl().matches(':popover-open');
    }

    /**
     * 서브메뉴를 가지고 있는지 확인한다.
     *
     * @returns 서브메뉴 가지고 있는지 여부
     */
    hasSubmenu(): boolean {
        return this.menus.some((item) => {
            return 'menu' in item && item.menu !== undefined && item.menu !== null;
        });
    }

    /**
     * 메뉴에 포커스를 지정한다.
     *
     * @param is_item_focus - 메뉴에 포커스를 지정할때 첫번째 메뉴아이템에 포커스를 줄지 여부
     */
    focus(is_item_focus: boolean = false): void {
        if (is_item_focus == true) {
            /**
             * 메뉴 아이템 중에 포커스를 가진 메뉴가 존재하는지 확인하여 없을 경우 첫번째 메뉴아이템에 포커스를 준다.
             */
            for (const item of this.menus) {
                if (item.focus() === true) {
                    break;
                }
            }
        } else {
            this.$getComponent().focus();
        }
    }

    /**
     * 메뉴 컴포넌트의 DOM 을 문서에 연결한다.
     */
    async connect(): Promise<void> {
        if (this.$getComponent().isConnected() == false) {
            if (this.getParent() instanceof Menu) {
                this.getParent().$getComponent().append(this.$getComponent());
            } else {
                if (this.$target !== null && this.$target.getParents('aui-component') !== null) {
                    this.$target.getParents('aui-component').append(this.$getComponent());
                } else {
                    Html.get('*[data-role=aui]').append(this.$getComponent());
                }
            }

            await Renderer.request(() => this.render());
        }
    }

    /**
     * 헤더를 렌더링한다.
     */
    async renderHeader(): Promise<void> {
        if (this.title !== null) {
            this.$getHeader().append(this.title.$getElement());
            await this.title.render();
        }
    }

    /**
     * 메뉴아이템을 렌더링한다.
     */
    async renderItems(): Promise<void> {
        for (const item of this.menus ?? []) {
            this.$getContent().append(item.$getElement());
            await item.render();
        }
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        this.$getContainer().append(this.$getHeader());
        this.$getContainer().append(this.$getContent());

        await this.renderHeader();
        await this.renderItems();
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        this.$getComponent().addClass('rendered');
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.title?.remove(false);
        this.title = null;
        this.$header = null;

        for (const item of this.menus ?? []) {
            item.remove(false);
        }
        this.menus = [];

        await super.remove(removeDom);
    }

    /**
     * 키보드 이벤트를 처리한다.
     *
     * @param e - 키보드 이벤트
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onKeyboard(e: KeyboardEvent): Promise<boolean> {
        if (e.key == 'ArrowUp') {
            const currentIndex = this.menus.findIndex((item) => item.isFocused());

            const startIndex = currentIndex === -1 ? this.menus.length - 1 : currentIndex - 1;
            for (let i = startIndex; i >= 0; i--) {
                if (this.menus[i].focus() === true) {
                    break;
                }
            }

            return false;
        }

        if (e.key == 'ArrowDown') {
            const currentIndex = this.menus.findIndex((item) => item.isFocused());

            for (let i = currentIndex + 1; i < this.menus.length; i++) {
                if (this.menus[i].focus() === true) {
                    break;
                }
            }

            return false;
        }

        if (e.key == 'Escape' && this.isShow() == true) {
            await this.hide();
            return false;
        }

        await this.fireEvent('keyboard', [e, this]);
        return false;
    }
}
